import React, { useEffect, useState } from 'react';
import { Smartphone, Chrome } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { motion, Variants } from 'framer-motion';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface ActionItem {
  icon: React.ReactNode;
  title: string;
  description: string;
  iconBg: string;
  href: string;
}

interface UpgradeSuccessDialogProps {
  onClose: () => void;
  onExploreFeatures?: () => void;
}

const AppleIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
  </svg>
);

const UpgradeSuccessDialog: React.FC<UpgradeSuccessDialogProps> = ({
  onClose,
}) => {
  const { t } = useTranslation();
  const [isMac, setIsMac] = useState(false);

  useEffect(() => {
    const platform = navigator.platform || navigator.userAgent;
    setIsMac(/Mac|iPhone|iPod|iPad/i.test(platform));
  }, []);

  useEffect(() => {
    const returnTo = sessionStorage.getItem('externalUpgradeReturn');
    if (!returnTo) return;

    sessionStorage.removeItem('externalUpgradeReturn');
    const redirectTimeout = window.setTimeout(() => {
      window.location.href = returnTo;
    }, 1200);

    // eslint-disable-next-line consistent-return
    return () => clearTimeout(redirectTimeout);
  }, []);

  const actions: ActionItem[] = [
    ...(isMac
      ? [
          {
            icon: <AppleIcon className="h-4 w-4" />,
            title: 'Download Mac App',
            description: 'Get the native Mac experience with our desktop app',
            iconBg: 'bg-gray-900 dark:bg-gray-700',
            href: '/desktop?utm_source=upgrade_success&utm_medium=dialog&utm_campaign=mac_app',
          },
        ]
      : []),
    {
      icon: <Smartphone className="h-4 w-4" />,
      title: t('upgrade.success.actions.recordedDemo.title'),
      description: t('upgrade.success.actions.recordedDemo.description'),
      iconBg: 'bg-blue-500',
      href: 'https://screenapp.io/mobile?utm_source=upgrade_success&utm_medium=dialog&utm_campaign=mobile_app',
    },
    {
      icon: <Chrome className="h-4 w-4" />,
      title: t('upgrade.success.actions.chromeExtension.title'),
      description: t('upgrade.success.actions.chromeExtension.description'),
      iconBg: 'bg-green-500',
      href: 'https://screenapp.io/chrome?utm_source=upgrade_success&utm_medium=dialog&utm_campaign=chrome_extension',
    },
  ];

  const containerVariants: Variants = {
    hidden: {
      scale: 0.8,
      opacity: 0,
      rotate: -30,
    },
    visible: {
      scale: 1,
      opacity: 1,
      rotate: 0,
      transition: {
        type: 'spring' as const,
        stiffness: 300,
        damping: 24,
        mass: 0.8,
      },
    },
  };

  const circleVariants: Variants = {
    hidden: {
      pathLength: 0,
      opacity: 0,
    },
    visible: {
      pathLength: 1,
      opacity: 1,
      transition: {
        pathLength: {
          type: 'tween' as const,
          duration: 0.5,
          ease: 'easeOut',
        },
        opacity: {
          duration: 0.01,
        },
      },
    },
  };

  const tickVariants: Variants = {
    hidden: {
      pathLength: 0,
      opacity: 0,
    },
    visible: {
      pathLength: 1,
      opacity: 1,
      transition: {
        pathLength: {
          type: 'spring' as const,
          stiffness: 400,
          damping: 22,
          mass: 0.5,
          delay: 0.35,
        },
        opacity: {
          duration: 0.01,
          delay: 0.35,
        },
      },
    },
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md p-0 gap-0">
        <DialogHeader className="p-6 pb-0 text-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={containerVariants}
            className="mx-auto mb-4"
          >
            <svg
              width="72"
              height="72"
              viewBox="0 0 72 72"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <motion.circle
                cx="36"
                cy="36"
                r="32"
                stroke="rgb(34 197 94)"
                strokeWidth="4"
                fill="none"
                variants={circleVariants}
                initial="hidden"
                animate="visible"
              />
              <motion.path
                d="M20 36 L30 46 L52 24"
                stroke="rgb(34 197 94)"
                strokeWidth="5"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                variants={tickVariants}
                initial="hidden"
                animate="visible"
              />
            </svg>
          </motion.div>
          <DialogTitle className="text-xl text-center">
            You&apos;re All Set!
          </DialogTitle>
        </DialogHeader>

        <div className="p-6 space-y-4">
          {actions.length > 0 && (
            <>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">
                    {t('upgrade.success.whatsNext')}
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                {actions.map((action, index) => (
                  <a
                    key={index}
                    href={action.href}
                    target="_blank"
                    rel="noreferrer"
                    className={cn(
                      'flex items-center gap-3 p-3 rounded-lg',
                      'border border-transparent',
                      'transition-all hover:bg-muted/50 hover:border-border',
                      'no-underline group',
                    )}
                  >
                    <div
                      className={cn(
                        'flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center text-white',
                        action.iconBg,
                      )}
                    >
                      {action.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                        {action.title}
                      </h4>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {action.description}
                      </p>
                    </div>
                  </a>
                ))}
              </div>
            </>
          )}
        </div>

        <DialogFooter className="p-6 pt-0">
          <Button
            onClick={onClose}
            className="w-full"
            size="lg"
            data-testid="upgrade-success-close-button"
          >
            Got it
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UpgradeSuccessDialog;
